import { Loader2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type AuthFormFieldsProps = {
  isLogin: boolean;
  email: string;
  password: string;
  displayName: string;
  errors: Record<string, string>;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onDisplayNameChange: (value: string) => void;
  onGetSuggestions: () => void;
  loadingSuggestions: boolean;
  avatarUrl?: string | null;
  generatingAvatar?: boolean;
  showAvatarPreview?: boolean;
};

export const AuthFormFields = ({
  isLogin,
  email,
  password,
  displayName,
  errors,
  onEmailChange,
  onPasswordChange,
  onDisplayNameChange,
  onGetSuggestions,
  loadingSuggestions,
  avatarUrl,
  generatingAvatar,
  showAvatarPreview = false,
}: AuthFormFieldsProps) => {
  return (
    <>
      {!isLogin && (
        <div className="space-y-1.5">
          <Label htmlFor="displayName">
            Display Name
            <span className="block text-xs font-normal text-muted-foreground mt-0.5">
              or just type some words of inspiration and let the bot help
            </span>
          </Label>
          <div className={showAvatarPreview ? "flex gap-2 items-start" : "flex gap-2"}>
            {showAvatarPreview && (avatarUrl || generatingAvatar) && (
              <Avatar className="h-10 w-10 shrink-0">
                {generatingAvatar ? (
                  <AvatarFallback>
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </AvatarFallback>
                ) : (
                  <AvatarImage src={avatarUrl || undefined} alt="Avatar preview" />
                )}
              </Avatar>
            )}
            <div className="flex gap-2 flex-1">
              <Input
                id="displayName"
                type="text"
                placeholder="blue gymnastics soccer dance transport"
                value={displayName}
                onChange={(e) => onDisplayNameChange(e.target.value)}
                maxLength={200}
                required
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onGetSuggestions}
                disabled={!displayName.trim() || loadingSuggestions}
                className="shrink-0"
              >
                {loadingSuggestions ? <Loader2 className="h-4 w-4 animate-spin" /> : "✨ AI"}
              </Button>
            </div>
          </div>
          {errors.displayName && (
            <p className="text-sm text-destructive">
              {errors.displayName}
              {(errors.displayName.includes("characters") || errors.displayName.includes("contain")) &&
                " Try AI suggestions."}
            </p>
          )}
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          required
        />
        {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          required
        />
        {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
        {!isLogin && (
          <p className="text-xs text-muted-foreground">
            Password must be at least 8 characters with uppercase, lowercase, number, and special character.
          </p>
        )}
      </div>
    </>
  );
};
